'use client';

/**
 * Telegram/WhatsApp-style fullscreen photo viewer.
 *
 * Open / close are FLIP transitions between the clicked thumbnail's
 * bounding rect and the centred viewport-fit rect. The trick to make
 * them look continuous (no flash of the bubble background under the
 * source) is the "baton-pass":
 *
 *   open  : lightbox img mounts AT srcRect → waits for the image to
 *           actually decode → hides the source → starts the zoom out.
 *   close : zoom in to srcRect → restores the source synchronously →
 *           unmounts the lightbox in the SAME tick.
 *
 * Both endpoints have one image continuously visible at the same
 * position, so the source/destination swap is invisible.
 *
 * Gestures: pinch to zoom (1×–5×), single-finger pan once zoomed,
 * single-finger swipe-down at scale 1 dismisses with a backdrop fade.
 * Wheel zoom on desktop, ESC / X / backdrop click also dismiss.
 *
 * The host component is mounted once in `(app)/layout.tsx`; pages
 * trigger it via `openLightbox()` in `lib/store/lightbox.ts`.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLightbox } from '@splace/core';

const ENTER_MS = 280;
const EXIT_MS  = 240;
const SWIPE_DISMISS_PX = 120;
const MIN_SCALE = 1;
const MAX_SCALE = 5;

interface Rect { left: number; top: number; width: number; height: number }

function computeTargetRect(naturalW: number, naturalH: number): Rect {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const aspect = naturalW / naturalH || 1;
  let w = vw;
  let h = vw / aspect;
  if (h > vh) { h = vh; w = vh * aspect; }
  return {
    left: (vw - w) / 2,
    top:  (vh - h) / 2,
    width: w, height: h,
  };
}

function flipTransform(target: Rect, src: DOMRect): string {
  const tCx = target.left + target.width / 2;
  const tCy = target.top  + target.height / 2;
  const sCx = src.left + src.width / 2;
  const sCy = src.top  + src.height / 2;
  const s = src.width / target.width;
  return `translate(${sCx - tCx}px, ${sCy - tCy}px) scale(${s})`;
}

interface BubbleRadii { tl: number; tr: number; br: number; bl: number }

/**
 * Read the source element's computed border-radius (4 corners) and
 * scale each value by 1/s, where s = srcWidth / targetWidth — the
 * scale the lightbox img is at while parked at srcRect. This way
 * `displayed_radius = stored_radius * s` matches the bubble exactly
 * at the start of the open FLIP. Animating the stored radii to 0
 * while the transform animates to identity collapses corners to
 * sharp 0 by the time the image reaches fullscreen.
 */
function readScaledRadii(srcEl: HTMLElement | undefined, scale: number): BubbleRadii {
  if (!srcEl) return { tl: 0, tr: 0, br: 0, bl: 0 };
  const cs = window.getComputedStyle(srcEl);
  const r = (raw: string) => (parseFloat(raw) || 0) / scale;
  return {
    tl: r(cs.borderTopLeftRadius),
    tr: r(cs.borderTopRightRadius),
    br: r(cs.borderBottomRightRadius),
    bl: r(cs.borderBottomLeftRadius),
  };
}

function applyRadii(el: HTMLElement, r: BubbleRadii) {
  el.style.borderTopLeftRadius     = `${r.tl}px`;
  el.style.borderTopRightRadius    = `${r.tr}px`;
  el.style.borderBottomRightRadius = `${r.br}px`;
  el.style.borderBottomLeftRadius  = `${r.bl}px`;
}

export function PhotoLightbox() {
  const current = useLightbox(s => s.current);
  const closeStore = useLightbox(s => s.close);

  const [mounted, setMounted] = useState<typeof current>(null);
  const [phase, setPhase] = useState<'opening' | 'open' | 'closing'>('opening');
  // Toggled true once the new <img> has decoded (or was already in
  // cache). Open FLIP is gated on this so the source is never hidden
  // while the destination would still be empty.
  const [imgReady, setImgReady] = useState(false);

  const imgRef = useRef<HTMLImageElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<Rect | null>(null);
  // Direct DOM control over the source thumbnail's visibility — we
  // hide/restore it at exact lifecycle points (just before the open
  // FLIP starts; just before unmount on close), which the React effect
  // cleanup pattern doesn't give us with the right ordering.
  const sourceStateRef = useRef<{ el: HTMLElement; prev: string } | null>(null);

  function hideSource(el: HTMLElement) {
    if (sourceStateRef.current) return;
    sourceStateRef.current = { el, prev: el.style.visibility };
    el.style.visibility = 'hidden';
  }
  function restoreSource() {
    const s = sourceStateRef.current;
    if (!s) return;
    s.el.style.visibility = s.prev;
    sourceStateRef.current = null;
  }

  // Gesture state in a ref — handlers commit transforms via inline
  // style without re-rendering on every move event.
  const gestureRef = useRef<{
    scale: number; tx: number; ty: number;
    pinchStartDist: number; pinchStartScale: number;
    pinchStartMidX: number; pinchStartMidY: number;
    pinchStartTx: number; pinchStartTy: number;
    panStartX: number; panStartY: number;
    panStartTx: number; panStartTy: number;
    mode: 'none' | 'pan' | 'pinch' | 'swipe-dismiss';
  }>({
    scale: 1, tx: 0, ty: 0,
    pinchStartDist: 0, pinchStartScale: 1,
    pinchStartMidX: 0, pinchStartMidY: 0,
    pinchStartTx: 0, pinchStartTy: 0,
    panStartX: 0, panStartY: 0,
    panStartTx: 0, panStartTy: 0,
    mode: 'none',
  });

  // ── Sync store → local state ──
  useEffect(() => {
    if (current && !mounted) {
      gestureRef.current.scale = 1;
      gestureRef.current.tx = 0;
      gestureRef.current.ty = 0;
      gestureRef.current.mode = 'none';
      setMounted(current);
      setPhase('opening');
      setImgReady(false);
    } else if (!current && mounted) {
      // Programmatic close — skip animation, restore source immediately.
      restoreSource();
      setMounted(null);
    }
  }, [current, mounted]);

  // ── ESC ──
  useEffect(() => {
    if (!mounted) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') startClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mounted]);

  function startClose() {
    if (phase === 'closing') return;
    setPhase('closing');
  }

  // ── If the <img> is already decoded synchronously (cached), the
  // onLoad listener may have run before the effect attached. Catch
  // that case by checking `complete` after mount. ──
  useEffect(() => {
    if (!mounted) return;
    const el = imgRef.current;
    if (el?.complete && el.naturalWidth > 0) setImgReady(true);
  }, [mounted]);

  // ── Open FLIP — runs once when the destination image is ready ──
  // Photo zoom is "dry" (transform only, no opacity transition on the
  // image itself); the backdrop fades 0→1 in parallel so the chat
  // doesn't snap to black abruptly behind the zoom.
  useLayoutEffect(() => {
    if (!mounted || phase !== 'opening' || !imgReady) return;
    const el = imgRef.current;
    const bd = backdropRef.current;
    const target = targetRef.current;
    if (!el || !bd || !target) return;

    // Plant the lightbox <img> at exactly the source rect (no
    // transition). At this instant both images are visible at the same
    // spot — content matches, so they overlap pixel-perfect.
    const scale = mounted.srcRect.width / target.width;
    const startRadii = readScaledRadii(mounted.srcEl, scale);
    el.style.transition = 'none';
    el.style.transform  = flipTransform(target, mounted.srcRect);
    applyRadii(el, startRadii);
    bd.style.transition = 'none';
    bd.style.opacity    = '0';
    void el.offsetHeight;

    // Now hide the source — the lightbox is on top with the same
    // content, so the user sees no change.
    if (mounted.srcEl) hideSource(mounted.srcEl);

    requestAnimationFrame(() => {
      el.style.transition = `transform ${ENTER_MS}ms cubic-bezier(.2,.8,.2,1), border-radius ${ENTER_MS}ms ease`;
      el.style.transform  = '';
      applyRadii(el, { tl: 0, tr: 0, br: 0, bl: 0 });
      bd.style.transition = `opacity ${ENTER_MS}ms ease`;
      bd.style.opacity    = '1';
    });

    const t = setTimeout(() => setPhase('open'), ENTER_MS + 20);
    return () => clearTimeout(t);
  }, [mounted, phase, imgReady]);

  // ── Close FLIP — animate back, restore source, unmount in one tick ──
  // Photo zoom stays "dry"; backdrop fades 1→0 in parallel so the chat
  // re-appears smoothly under it. With the source bubble fully hidden
  // (whole bubble div, not just the img) there's no red rectangle to
  // expose during the fade.
  useEffect(() => {
    if (!mounted || phase !== 'closing') return;
    const el = imgRef.current;
    const bd = backdropRef.current;
    const target = targetRef.current;

    // No image yet (closed before load) → nothing to animate, just
    // tear down. Source was never hidden in this case.
    if (!el || !bd || !target || !imgReady) {
      restoreSource();
      setMounted(null);
      closeStore();
      return;
    }

    const scale = mounted.srcRect.width / target.width;
    const endRadii = readScaledRadii(mounted.srcEl, scale);
    el.style.transition = `transform ${EXIT_MS}ms ease, border-radius ${EXIT_MS}ms ease`;
    el.style.transform  = flipTransform(target, mounted.srcRect);
    applyRadii(el, endRadii);
    bd.style.transition = `opacity ${EXIT_MS}ms ease`;
    bd.style.opacity    = '0';

    const t = setTimeout(() => {
      // Restore the source BEFORE unmount: at this instant the
      // lightbox img is parked at srcRect with the same content as the
      // source. Making the source visible while the lightbox is still
      // there means there's no frame with neither visible.
      restoreSource();
      setMounted(null);
      closeStore();
    }, EXIT_MS + 20);
    return () => clearTimeout(t);
  }, [phase, mounted, imgReady, closeStore]);

  // ── Recompute target rect on resize while open ──
  useEffect(() => {
    if (!mounted) { targetRef.current = null; return; }
    const compute = () => {
      const w = mounted.naturalWidth  || mounted.srcRect.width;
      const h = mounted.naturalHeight || mounted.srcRect.height;
      targetRef.current = computeTargetRect(w, h);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [mounted]);

  // ── Touch / wheel handlers ──
  function applyTransform() {
    const el = imgRef.current;
    if (!el) return;
    const g = gestureRef.current;
    el.style.transition = 'none';
    el.style.transform  = `translate(${g.tx}px, ${g.ty}px) scale(${g.scale})`;
    if (g.mode === 'swipe-dismiss' && backdropRef.current) {
      const dy = Math.max(0, g.ty);
      const opacity = Math.max(0, 1 - dy / (SWIPE_DISMISS_PX * 3));
      backdropRef.current.style.transition = 'none';
      backdropRef.current.style.opacity    = String(opacity);
    }
  }

  function settleTransform(animate = true) {
    const el = imgRef.current;
    if (!el) return;
    const g = gestureRef.current;
    if (animate) el.style.transition = 'transform .22s ease';
    if (g.scale < MIN_SCALE) { g.scale = MIN_SCALE; g.tx = 0; g.ty = 0; }
    else if (g.scale > MAX_SCALE) { g.scale = MAX_SCALE; }
    el.style.transform = `translate(${g.tx}px, ${g.ty}px) scale(${g.scale})`;
    if (backdropRef.current) {
      backdropRef.current.style.transition = 'opacity .22s ease';
      backdropRef.current.style.opacity = '1';
    }
  }

  function onTouchStart(e: React.TouchEvent) {
    const g = gestureRef.current;
    if (e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      const dx = a.clientX - b.clientX;
      const dy = a.clientY - b.clientY;
      g.pinchStartDist  = Math.hypot(dx, dy);
      g.pinchStartScale = g.scale;
      g.pinchStartMidX  = (a.clientX + b.clientX) / 2;
      g.pinchStartMidY  = (a.clientY + b.clientY) / 2;
      g.pinchStartTx    = g.tx;
      g.pinchStartTy    = g.ty;
      g.mode = 'pinch';
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      g.panStartX = t.clientX;
      g.panStartY = t.clientY;
      g.panStartTx = g.tx;
      g.panStartTy = g.ty;
      g.mode = g.scale > 1 ? 'pan' : 'swipe-dismiss';
    }
  }

  function onTouchMove(e: React.TouchEvent) {
    const g = gestureRef.current;
    if (g.mode === 'pinch' && e.touches.length === 2) {
      e.preventDefault();
      const [a, b] = [e.touches[0], e.touches[1]];
      const dx = a.clientX - b.clientX;
      const dy = a.clientY - b.clientY;
      const dist = Math.hypot(dx, dy);
      const newScale = Math.max(0.5, Math.min(MAX_SCALE, g.pinchStartScale * (dist / (g.pinchStartDist || 1))));
      const midX = (a.clientX + b.clientX) / 2;
      const midY = (a.clientY + b.clientY) / 2;
      g.tx = g.pinchStartTx + (midX - g.pinchStartMidX);
      g.ty = g.pinchStartTy + (midY - g.pinchStartMidY);
      g.scale = newScale;
      applyTransform();
    } else if ((g.mode === 'pan' || g.mode === 'swipe-dismiss') && e.touches.length === 1) {
      const t = e.touches[0];
      const dx = t.clientX - g.panStartX;
      const dy = t.clientY - g.panStartY;
      if (g.mode === 'swipe-dismiss') {
        g.tx = 0;
        g.ty = Math.max(0, dy);
        const k = Math.max(0, 1 - g.ty / 1200);
        g.scale = k;
      } else {
        g.tx = g.panStartTx + dx;
        g.ty = g.panStartTy + dy;
      }
      applyTransform();
    }
  }

  function onTouchEnd() {
    const g = gestureRef.current;
    if (g.mode === 'swipe-dismiss' && Math.abs(g.ty) > SWIPE_DISMISS_PX) {
      g.scale = 1; g.tx = 0; g.ty = 0;
      startClose();
      return;
    }
    g.mode = 'none';
    if (g.scale < MIN_SCALE) { g.scale = MIN_SCALE; g.tx = 0; g.ty = 0; }
    settleTransform(true);
  }

  function onWheel(e: React.WheelEvent) {
    if (!mounted) return;
    e.preventDefault();
    const g = gestureRef.current;
    const delta = -e.deltaY * 0.0025;
    const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, g.scale * (1 + delta)));
    g.scale = newScale;
    if (newScale === MIN_SCALE) { g.tx = 0; g.ty = 0; }
    applyTransform();
  }

  if (!mounted) return null;
  if (typeof document === 'undefined') return null;

  const w = mounted.naturalWidth  || mounted.srcRect.width;
  const h = mounted.naturalHeight || mounted.srcRect.height;
  const target = targetRef.current ?? computeTargetRect(w, h);
  targetRef.current = target;

  return createPortal(
    // Outer is transparent and handles backdrop-click + wheel zoom.
    // The black `<div ref={backdropRef}>` is a SIBLING of the photo
    // (not its parent) so animating its opacity doesn't cascade to
    // the <img>: the photo's own opacity stays at 1 throughout.
    <div
      onClick={startClose}
      onWheel={onWheel}
      style={{
        position: 'fixed', inset: 0,
        zIndex: 9999,
        background: 'transparent',
        touchAction: 'none',
        userSelect: 'none',
      }}
    >
      <div
        ref={backdropRef}
        style={{
          position: 'absolute', inset: 0,
          background: 'rgba(0,0,0,1)',
          // Faded in/out by the open/close effects.
          opacity: 0,
          pointerEvents: 'none',
        }}
      />
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        ref={imgRef}
        src={mounted.src}
        alt=""
        draggable={false}
        onLoad={() => setImgReady(true)}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        style={{
          position: 'fixed',
          left: target.left, top: target.top,
          width: target.width, height: target.height,
          objectFit: 'contain',
          willChange: 'transform',
          transformOrigin: 'center center',
          cursor: 'zoom-out',
          // Until the open FLIP plants the lightbox at srcRect we
          // don't want the destination to flash at its full-size
          // position behind a transparent backdrop. Hidden pre-load,
          // visible the instant the open effect runs.
          visibility: imgReady ? 'visible' : 'hidden',
        }}
      />
      <button
        onClick={(e) => { e.stopPropagation(); startClose(); }}
        aria-label="Close"
        style={{
          position: 'fixed', top: 16, right: 16, zIndex: 10000,
          width: 40, height: 40, borderRadius: '50%',
          background: 'rgba(255,255,255,.12)', border: 'none',
          color: 'white', cursor: 'pointer', fontSize: 18,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(8px)',
        }}
      >
        ✕
      </button>
    </div>,
    document.body,
  );
}
